import { Component, OnInit, inject, signal, effect, computed, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { ArrangmentCronApiService } from '../../service/api-service.service';
import { ArrangmentCronDatePickerService } from '../../services/date-picker.service';
import { PieChartComponent } from '../../../../../shared/components/charts/pie-chart/pie-chart.component';
import { ContractStatusService } from '../../services/contract-status.service';
import { BarLineComponent } from '../../../../../shared/components/charts/bar-charts/bar-line/bar-line.component';
import { PolarAreaChartComponent } from '../../../../../shared/components/charts/polar-area-chart/polar-area-chart.component';
import { CylinderChartComponent } from '../../../../../shared/components/charts/bar-charts/cylinder-chart/cylinder-chart.component';
import { IsometricBarComponent } from '../../../../../shared/components/charts/isometric-bar/isometric-bar.component';
import { HorizontalBarChartComponent } from '../../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { DonutChartComponent } from '../../../../../shared/components/charts/donut-chart/donut-chart.component';
import { IndividualToasterComponent } from '../../../../../shared/components/loader/loader.component';
import { DatePickerComponent, type DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { DropdownComponent, type DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { AdminDetailPayload, AdminDetailAPI, ContractDetailsAPI } from '../../interface/arrangment-cron-api.interface';
import { ThemeService } from '../../../../../core/services/theme.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';
import { CurrencyPipe } from '@angular/common';

@Component({
  selector: 'app-admin-details',
  standalone: true,
  imports: [CommonModule, IconComponentV2, PieChartComponent, BarLineComponent, PolarAreaChartComponent, CylinderChartComponent, IsometricBarComponent, HorizontalBarChartComponent, DonutChartComponent, IndividualToasterComponent, DatePickerComponent, DropdownComponent],
  providers: [CurrencyPipe, ArrangmentCronDatePickerService],
  templateUrl: './admin-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class AdminDetailsComponent implements OnInit {
  public apiService = inject(ArrangmentCronApiService);
  public datePickerService = inject(ArrangmentCronDatePickerService);
  private contractStatusService = inject(ContractStatusService);
  private themeService = inject(ThemeService);
  private currencyPipe = inject(CurrencyPipe);

  currentTheme = signal<'light' | 'dark'>('dark');
  isLoading = signal(false);

  adminId = signal<number>(257);
  @Input('adminId') set _adminIdInput(val: number) {
    if (val) this.adminId.set(val);
  }
  @Input() adminList: DropdownOption[] = [];
  @Output() closePanel = new EventEmitter<void>();

  datePickerValue = computed(() => ({
    startDate: this.datePickerService.startDate(),
    endDate: this.datePickerService.endDate(),
    unit: this.datePickerService.unit() as any,
    comparisonType: 'none' as const
  }));

  onAdminChange(newId: string | number) {
    if (newId) this.adminId.set(Number(newId));
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    this.datePickerService.updateDateRange(event);
  }

  funnelData = signal<any[]>([]);
  performanceStats = signal<any[]>([]);
  contractStatusData = signal<any[]>([]);
  executionGapData = signal<any[]>([]);
  timelineData = signal<any[]>([]);
  successByValueData = signal<any[]>([]);
  conversionRate = signal<number>(0);
  totalCreated = signal<number>(0);

  dataQualityRiskData = signal<any[]>([
    { label: 'Null Entries', value: 78, color: '#6889f8' },
    { label: 'Duplicates', value: 58, color: '#00dc82' },
    { label: 'Deletion', value: 37, color: '#f5a829' }
  ]);

  performanceTrendTitle = signal<string>('Performance Trend by Year');
  performanceTrendSubtitle = signal<string>('Total Successful, Total Failed and Success % by Year');
  performanceTrendData = signal<any[]>([]);
  hasPerformanceTrendData = signal<boolean>(true);

  successArrangements = signal({
    totalRecovered: 0,
    recurring: 0,
    partial: 0,
    trend: 0
  });

  failedArrangements = signal({
    lossAmount: 0,
    recurring: 0,
    partial: 0,
    trend: 0
  });

  financialMetrics = signal({
    scheduledValue: 0,
    scheduledTrend: 0,
    recoveredValue: 0,
    recoveredTrend: 0,
    recoveryRate: 0,
    recoveryRateTrend: 0,
    perSuccess: 0,
    perSuccessTrend: 0,
    averageValue: 0,
    averageValueTrend: 0
  });

  get hasExecutionGapData(): boolean {
    return this.executionGapData().some(d => d.value > 0);
  }

  get contractStatusTotal(): number {
    return this.contractStatusData().reduce((s, d) => s + d.value, 0);
  }

  scorecard = signal({
    performanceScore: 0,
    successRate: 0,
    recoveryRate: 0,
    efficiencyScore: 'N/A',
    totalArrangements: 0,
    successfulArrangements: 0,
    failedArrangements: 0,
    uniqueContracts: 0,
    arrangementsPerContract: 0,
    avgDaysToExecution: 0,
  });

  scorecardCategories = computed(() => {
    const s = this.scorecard();
    const f = this.financialMetrics();
    const r = this.riskMetrics();

    return [
      {
        title: 'Performance Scores',
        metrics: [
          { label: 'Performance Score', value: s.performanceScore, valueColor: 'text-blue-600 dark:text-blue-400 font-semibold' },
          { label: 'Success Rate', value: `${(s.successRate || 0).toFixed(2)}%`, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Recovery Rate', value: `${(s.recoveryRate || 0).toFixed(2)}%`, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Efficiency Score', value: s.efficiencyScore, valueColor: `font-semibold ${s.efficiencyScore === 'Fast' ? 'text-emerald-500 dark:text-emerald-400' : s.efficiencyScore === 'Average' ? 'text-amber-500 dark:text-amber-400' : 'text-red-500 dark:text-red-400'}` }
        ]
      },
      {
        title: 'Volume Metrics',
        metrics: [
          { label: 'Total Arrangements', value: s.totalArrangements, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Successful Arrangements', value: s.successfulArrangements, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Failed Arrangements', value: s.failedArrangements, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Unique Contracts', value: s.uniqueContracts, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Arrangements per Contract', value: (s.arrangementsPerContract || 0).toFixed(2), valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Avg Days to Execution', value: (s.avgDaysToExecution || 0).toFixed(2), valueColor: 'text-gray-900 dark:text-white font-semibold' }
        ]
      },
      {
        title: 'Financial Overview',
        metrics: [
          { label: 'Total Scheduled', value: `$${(f.scheduledValue || 0).toFixed(2)}`, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Total Recovered', value: `$${(f.recoveredValue || 0).toFixed(2)}`, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Recovery per Success', value: `$${(f.perSuccess || 0).toFixed(2)}`, valueColor: 'text-gray-900 dark:text-white font-semibold' }
        ]
      },
      {
        title: 'Duplicate & Risk',
        metrics: [
          { label: 'Duplicate Rate', value: `${(r.duplicateRate || 0).toFixed(2)}%`, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Deletion Rate', value: `${(r.deletionRate || 0).toFixed(2)}%`, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Contracts with Duplicates', value: r.contractsWithDuplicates, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Duplicate Entries', value: r.duplicateEntries, valueColor: 'text-gray-900 dark:text-white font-semibold' },
          { label: 'Deletion Entries', value: r.deletionEntries, valueColor: 'text-gray-900 dark:text-white font-semibold' }
        ]
      }
    ];
  });

  riskMetrics = signal({
    duplicateRate: 0,
    deletionRate: 0,
    contractsWithDuplicates: 0,
    duplicateEntries: 0,
    deletionEntries: 0,
    delinquency90PlusContracts: 0,
    delinquency90PlusRate: 0,
    overdueRate: 0,
    portfolioHealth: 'Stable',
    partialSuccessRate: 0
  });

  recommendationCards = computed(() => {
    const metrics = this.riskMetrics();
    
    let healthColor = 'text-gray-900 dark:text-white';
    if (metrics.portfolioHealth === 'Healthy') healthColor = 'text-emerald-500';
    if (metrics.portfolioHealth === 'Stable') healthColor = 'text-amber-500';
    if (metrics.portfolioHealth === 'At Risk') healthColor = 'text-red-500';

    let healthTheme = {
      card: 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/30',
      iconWrap: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500',
      badge: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400',
    };
    let healthIcon = 'ShieldCheck';
    let healthSeverity = 'Healthy';

    if (metrics.portfolioHealth === 'Stable') {
      healthTheme = {
        card: 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30',
        iconWrap: 'bg-amber-100 dark:bg-amber-900/30 text-amber-500',
        badge: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
      };
      healthIcon = 'ShieldAlert';
      healthSeverity = 'Stable';
    } else if (metrics.portfolioHealth === 'At Risk') {
      healthTheme = {
        card: 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30',
        iconWrap: 'bg-red-100 dark:bg-red-900/30 text-red-500',
        badge: 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400',
      };
      healthIcon = 'ShieldX';
      healthSeverity = 'At Risk';
    }

    let duplicateTheme = { card: 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/30', iconWrap: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500', badge: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' };
    let duplicateSeverity = 'Low';
    if (metrics.duplicateRate > 5) {
      if (metrics.duplicateRate > 15) {
        duplicateTheme = { card: 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30', iconWrap: 'bg-red-100 dark:bg-red-900/30 text-red-500', badge: 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400' };
        duplicateSeverity = 'High';
      } else {
        duplicateTheme = { card: 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30', iconWrap: 'bg-amber-100 dark:bg-amber-900/30 text-amber-500', badge: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400' };
        duplicateSeverity = 'Medium';
      }
    }

    let partialTheme = { card: 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/30', iconWrap: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500', badge: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' };
    let partialSeverity = 'Optimal';
    if (metrics.partialSuccessRate < 10) {
      partialTheme = { card: 'bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30', iconWrap: 'bg-blue-100 dark:bg-blue-900/30 text-blue-500', badge: 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400' };
      partialSeverity = 'Attention';
    } else if (metrics.partialSuccessRate > 20) {
      partialTheme = { card: 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30', iconWrap: 'bg-amber-100 dark:bg-amber-900/30 text-amber-500', badge: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400' };
      partialSeverity = 'High';
    }

    let delinquencyTheme = { card: 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/30', iconWrap: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500', badge: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' };
    let delinquencySeverity = 'Low';
    if (metrics.delinquency90PlusRate > 10) {
      delinquencyTheme = { card: 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30', iconWrap: 'bg-red-100 dark:bg-red-900/30 text-red-500', badge: 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400' };
      delinquencySeverity = 'Critical';
    } else if (metrics.delinquency90PlusRate > 3) {
      delinquencyTheme = { card: 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30', iconWrap: 'bg-amber-100 dark:bg-amber-900/30 text-amber-500', badge: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400' };
      delinquencySeverity = 'Warning';
    }

    return [
      {
        themeClasses: duplicateTheme,
        icon: 'Folders',
        severity: duplicateSeverity,
        title: 'Duplicate Rate',
        value: `${metrics.duplicateRate.toFixed(1)}%`,
        valueColor: 'text-gray-900 dark:text-white',
        subValue: null,
        description: duplicateSeverity === 'High' ? 'Contracts contain significant duplicate entries, severely impacting processing efficiency and data integrity.' : (duplicateSeverity === 'Medium' ? 'Moderate duplicate rate detected. Review for data continuity.' : 'Duplicate instances are well within acceptable operating tolerances.'),
        action: duplicateSeverity === 'High' ? 'Implement duplicate prevention workflow.' : (duplicateSeverity === 'Medium' ? 'Schedule deduplication.' : 'No action required.')
      },
      {
        themeClasses: partialTheme,
        icon: 'Folders',
        severity: partialSeverity,
        title: 'Partial Success Rate',
        value: `${metrics.partialSuccessRate.toFixed(1)}%`,
        valueColor: 'text-gray-900 dark:text-white',
        subValue: null,
        description: partialSeverity === 'Attention' ? 'Partial success rate is significantly lower than recurring plans. Optimization could improve overall yield.' : (partialSeverity === 'High' ? 'Elevated partial successes indicate strong possibility of conversion failure. Intervene immediately.' : 'Partial plans are converting efficiently.'),
        action: partialSeverity === 'Attention' ? 'Start admin training program.' : (partialSeverity === 'High' ? 'Review partial plan terms.' : 'Maintain current approach.')
      },
      {
        themeClasses: delinquencyTheme,
        icon: 'ShieldAlert',
        severity: delinquencySeverity,
        title: '90+ Day Delinquency',
        value: `${metrics.delinquency90PlusRate.toFixed(1)}%`,
        valueColor: 'text-gray-900 dark:text-white',
        subValue: `${metrics.delinquency90PlusContracts} Contracts`,
        description: delinquencySeverity === 'Critical' ? 'A large portion of the portfolio is in the 90+ day bucket, representing an elevated non-recovery risk.' : (delinquencySeverity === 'Warning' ? 'Delinquency rates are trending upward. Intervene early to prevent drift to 90+ days.' : 'Delinquency strictly contained with high portfolio resilience.'),
        action: delinquencySeverity === 'Critical' ? 'Escalate to senior recovery team.' : (delinquencySeverity === 'Warning' ? 'Deploy early outreach.' : 'View performance chart.')
      },
      {
        themeClasses: healthTheme,
        icon: healthIcon,
        severity: healthSeverity,
        title: 'Portfolio Health',
        value: metrics.portfolioHealth,
        valueColor: healthColor,
        subValue: null,
        description: metrics.portfolioHealth === 'At Risk' ? 'Data risks indicate instability and require immediate structural correction to prevent data loss.' : (metrics.portfolioHealth === 'Stable' ? 'General portfolio progression remains within normal parameters with no immediate structural risks.' : 'Excellent portfolio consistency. Minimal errors found across recent tracking activities.'),
        action: 'View detailed report.'
      }
    ];
  });
  adminInfo = signal({
    name: '',
    id: '',
    role: 'Senior Administrator',
    initials: '',
    totalArrangements: '0',
    successRate: '0%',
    uniqueContracts: '0',
    performanceScore: '0'
  });

  kpis = [
    {
      label: 'Successful',
      value: '0',
      trend: '',
      trendIcon: 'TrendingUp',
      icon: 'CircleCheck',
      colorClass: 'text-[#15B268]',
      bgClass: 'bg-[#15B268]',
      iconBg: 'bg-[#15B268]',
      shadowClass: 'shadow-[0_8px_16px_-4px_rgba(21,178,104,0.4)]'
    },
    {
      label: 'Failed',
      value: '0',
      trend: '',
      trendIcon: 'TrendingDown',
      icon: 'CircleX',
      colorClass: 'text-[#F05050]',
      bgClass: 'bg-[#F05050]',
      iconBg: 'bg-[#F05050]',
      shadowClass: 'shadow-[0_8px_16px_-4px_rgba(240,80,80,0.4)]'
    },
    {
      label: 'Recovered',
      value: '$0',
      trend: '',
      trendIcon: 'TrendingUp',
      icon: 'DollarSign',
      colorClass: 'text-[#3B82F6]',
      bgClass: 'bg-[#3B82F6]',
      iconBg: 'bg-[#3B82F6]',
      shadowClass: 'shadow-[0_8px_16px_-4px_rgba(59,130,246,0.4)]'
    },
    {
      label: 'Avg Days to Execute',
      value: '0',
      trend: '',
      trendIcon: '',
      icon: 'Clock',
      colorClass: 'text-[#8B5CF6]',
      bgClass: 'bg-[#8B5CF6]',
      iconBg: 'bg-[#8B5CF6]',
      shadowClass: 'shadow-[0_8px_16px_-4px_rgba(139,92,246,0.4)]'
    },
    {
      label: 'Risk Index',
      value: 'LOW',
      trend: '',
      trendIcon: '',
      icon: 'TriangleAlert',
      colorClass: 'text-[#15B268]',
      bgClass: 'bg-[#15B268]',
      iconBg: 'bg-[#15B268]',
      shadowClass: 'shadow-[0_8px_16px_-4px_rgba(21,178,104,0.4)]'
    }
  ];

  constructor() {
    // Listen for date changes via effect
    effect(() => {
      this.datePickerService.startDate();
      this.datePickerService.endDate();
      this.datePickerService.unit();
      this.adminId();
      this.fetchAdminDetails();
    });

    // Handle theme initialization and changes
    this.currentTheme.set(this.themeService.getTheme());
    this.themeService.theme$.subscribe(theme => this.currentTheme.set(theme));
  }

  ngOnInit() {}

  fetchAdminDetails() {
    this.isLoading.set(true);
    
    const startStr = this.datePickerService.startDate();
    const endStr = this.datePickerService.endDate();
    
    const start = dayjs(startStr).tz(LA_TIMEZONE);
    const end = dayjs(endStr).tz(LA_TIMEZONE);
    const duration = end.diff(start, 'day') + 1;

    const prevStart = start.subtract(duration, 'day').format('YYYY-MM-DD');
    const prevEnd = start.subtract(1, 'day').format('YYYY-MM-DD');

    const currentPayload: AdminDetailPayload = {
      admin_id: this.adminId(), // dynamic context admin_id
      start_time: start.format('YYYY-MM-DD'),
      end_time: end.format('YYYY-MM-DD'),
      is_dummy: 0
    };

    const previousPayload: AdminDetailPayload = {
      ...currentPayload,
      start_time: prevStart,
      end_time: prevEnd
    };

    forkJoin({
      current: this.apiService.getAdminDetail(currentPayload),
      previous: this.apiService.getAdminDetail(previousPayload).pipe(
        catchError(() => of(null)) // Gracious fallback if previous fails
      )
    }).subscribe({
      next: ({ current, previous }) => {
        if (current) {
          this.mapAdminInfo(current);
          this.mapKpiData(current, previous);
          this.mapFunnelData(current);
          this.mapPerformanceData(current);
          this.mapExecutionGapData(current);
          this.mapFinancialData(current, previous);
          this.mapSuccessByValueData(current);
          this.updateDynamicPerformanceTrend(current);
          this.updateScorecardFromAdmin(current);
          this.fetchContractDetailsAndMapStatus();
          this.fetchDuplicatesData();
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching admin details:', err);
        this.isLoading.set(false);
      }
    });
  }

  private mapFinancialData(currentRes: AdminDetailAPI, prevRes: AdminDetailAPI | null) {
    const curSf = currentRes.success_failure;
    const prevSf = prevRes?.success_failure;

    if (curSf) {
      const extractCounts = (typeData: any) => {
        return {
          recurring: typeData?.recurring_count || 0,
          partial: typeData?.partial_count || 0
        };
      };

      const succCounts = extractCounts(curSf.successful);
      const failCounts = extractCounts(curSf.failed);

      // Helper to calculate percentage trend
      const getTrend = (current: number, previous: number) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Number((((current - previous) / previous) * 100).toFixed(1));
      };

      // Current values
      const curSuccVal = curSf.successful?.total_amount || 0;
      const curFailVal = curSf.failed?.total_amount || 0;
      const curSchedVal = curSuccVal + curFailVal;
      const curRecRate = curSchedVal > 0 ? (curSuccVal / curSchedVal) * 100 : 0;
      const curPerSuccess = (curSf.successful?.count || 0) > 0 ? curSuccVal / curSf.successful.count : 0;
      const curTotalCount = (curSf.successful?.count || 0) + (curSf.failed?.count || 0);
      const curAvgVal = curTotalCount > 0 ? curSchedVal / curTotalCount : 0;

      // Update basic scorecard metrics tied to admin detail
      this.scorecard.update(s => ({
        ...s,
        recoveryRate: curRecRate
      }));

      // Previous values
      const prevSuccVal = prevSf?.successful?.total_amount || 0;
      const prevFailVal = prevSf?.failed?.total_amount || 0;
      const prevSchedVal = prevSuccVal + prevFailVal;
      const prevRecRate = prevSchedVal > 0 ? (prevSuccVal / prevSchedVal) * 100 : 0;
      const prevPerSuccess = (prevSf?.successful?.count || 0) > 0 ? prevSuccVal / prevSf!.successful!.count : 0;
      const prevTotalCount = (prevSf?.successful?.count || 0) + (prevSf?.failed?.count || 0);
      const prevAvgVal = prevTotalCount > 0 ? prevSchedVal / prevTotalCount : 0;

      this.successArrangements.set({
        totalRecovered: curSuccVal,
        recurring: succCounts.recurring,
        partial: succCounts.partial,
        trend: getTrend(curSuccVal, prevSuccVal)
      });

      this.failedArrangements.set({
        lossAmount: curFailVal,
        recurring: failCounts.recurring,
        partial: failCounts.partial,
        trend: getTrend(curFailVal, prevFailVal)
      });

      this.financialMetrics.set({
        scheduledValue: curSchedVal,
        scheduledTrend: getTrend(curSchedVal, prevSchedVal),
        recoveredValue: curSuccVal,
        recoveredTrend: getTrend(curSuccVal, prevSuccVal),
        recoveryRate: curRecRate,
        recoveryRateTrend: getTrend(curRecRate, prevRecRate),
        perSuccess: curPerSuccess,
        perSuccessTrend: getTrend(curPerSuccess, prevPerSuccess),
        averageValue: curAvgVal,
        averageValueTrend: getTrend(curAvgVal, prevAvgVal)
      });
    }
  }

  private mapAdminInfo(res: AdminDetailAPI) {
    const info = res.admin_info || {};
    const stats = res.summary || {};
    const name = info.admin_name || 'N/A';
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
    
    this.adminInfo.set({
      name: name,
      id: info.admin_id?.toString() || '0',
      role: 'Administrator',
      initials: initials,
      totalArrangements: stats.total_created?.toString() || '0',
      successRate: `${(stats.success_rate || 0).toFixed(2)}%`,
      uniqueContracts: stats.far_future_count?.toString() || '0', // Will overwrite gracefully with contract API
      performanceScore: Math.round(stats.success_rate || 0).toString()
    });
  }

  private updateScorecardFromAdmin(res: AdminDetailAPI) {
    const stats = res.summary || {};
    
    let efficiency = 'Average';
    if (stats.avg_days_to_execution < 30) efficiency = 'Fast';
    else if (stats.avg_days_to_execution > 60) efficiency = 'Slow';

    const successfulAttempts = res.success_failure?.successful?.count || 0;
    const partialSuccessCount = res.success_failure?.successful?.by_payment_type?.ach || res.success_failure?.successful?.by_payment_type?.card || 0; // fallback proxy since API doesn't have partial/recurring for count
    const partialSuccessRate = successfulAttempts > 0 ? (partialSuccessCount / successfulAttempts) * 100 : 0;

    this.scorecard.update(s => ({
      ...s,
      performanceScore: Math.round(stats.success_rate || 0),
      successRate: stats.success_rate || 0,
      efficiencyScore: efficiency,
      totalArrangements: stats.total_created || 0,
      successfulArrangements: stats.successful || 0,
      failedArrangements: stats.failed || 0,
      avgDaysToExecution: stats.avg_days_to_execution || 0
    }));

    this.riskMetrics.update(r => ({
      ...r,
      partialSuccessRate: partialSuccessRate
    }));
  }

  private mapKpiData(res: AdminDetailAPI, prev: AdminDetailAPI | null) {
    const stats: any = res.summary || {};
    const prevStats: any = prev?.summary || {};

    const getTrendInfo = (current: number, previous: number) => {
      let percent = 0;
      if (previous === 0) {
        percent = current > 0 ? 100 : 0;
      } else {
        percent = ((current - previous) / previous) * 100;
      }
      
      const isPositive = percent > 0;
      const isNegative = percent < 0;
      const isZero = percent === 0;

      let trendIcon = '';
      let colorClass = 'text-gray-500';

      if (isPositive) {
        trendIcon = 'TrendingUp';
        colorClass = 'text-[#15B268]'; // Green
      } else if (isNegative) {
        trendIcon = 'TrendingDown';
        colorClass = 'text-[#F05050]'; // Red
      } else {
        trendIcon = 'Minus';
      }

      return {
        text: `${Math.abs(percent).toFixed(1)}%`,
        icon: trendIcon,
        color: colorClass
      };
    };

    const sTrend = getTrendInfo(stats.successful || 0, prevStats.successful || 0);
    this.kpis[0].value = (stats.successful ?? 0).toString();
    this.kpis[0].trend = sTrend.text;
    this.kpis[0].trendIcon = sTrend.icon;
    this.kpis[0].colorClass = sTrend.color;

    const fTrend = getTrendInfo(stats.failed || 0, prevStats.failed || 0);
    this.kpis[1].value = (stats.failed ?? 0).toString();
    this.kpis[1].trend = fTrend.text;
    this.kpis[1].trendIcon = fTrend.icon;
    // For failed, we might want to invert colors (more fails = red), but preserving generic logic for now
    this.kpis[1].colorClass = (fTrend.color === 'text-[#15B268]') ? 'text-[#F05050]' : (fTrend.color === 'text-[#F05050]' ? 'text-[#15B268]' : fTrend.color);

    const rTrend = getTrendInfo(stats.total_recovered_amount || 0, prevStats.total_recovered_amount || 0);
    this.kpis[2].value = this.currencyPipe.transform(stats.total_recovered_amount || 0, 'USD', 'symbol', '1.2-2') || '$0.00';
    this.kpis[2].trend = rTrend.text;
    this.kpis[2].trendIcon = rTrend.icon;
    this.kpis[2].colorClass = rTrend.color;

    const avgDays = stats.avg_days_to_execution || 0;
    let speedLabel = 'Slow';
    if (avgDays == 0) speedLabel = '';
    else if (avgDays <= 7) speedLabel = 'Very Fast';
    else if (avgDays <= 14) speedLabel = 'Fast';

    this.kpis[3].value = avgDays.toFixed(2);
    this.kpis[3].trend = speedLabel;
    this.kpis[3].trendIcon = '';
    this.kpis[3].colorClass = 'text-[#8B5CF6]'; // Matching the base purple theme

    // Risk Index logic moved to updateRiskMetricsFromContractData to fix API race condition
  }

  private mapFunnelData(res: AdminDetailAPI) {
    const stats = res.summary || {};
    const total = stats.total_created || 0;
    const executed = stats.total_executed || 0;
    const remaining = Math.max(0, total - executed);
    const executionRate = total > 0 ? (executed / total) * 100 : 0;

    this.totalCreated.set(total);
    this.conversionRate.set(Math.round(executionRate));
    this.funnelData.set([
      { label: 'Executed', value: executed, color: '#ec4899' },
      { label: 'Total', value: remaining, color: '#EAEAEA', displayValue: total, hidePercentage: true }
    ]);
  }

  private mapPerformanceData(res: AdminDetailAPI) {
    const records = res.creation_trend?.records || [];
    this.performanceStats.set(records.map((r: any) => ({
      ...r,
      total_created: r.created ?? r.total_created ?? 0,
      success_rate: r.success_rate ?? 0
    })));
  }

  private fetchContractDetailsAndMapStatus() {
    const payload = {
      start_time: dayjs(this.datePickerService.startDate()).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      end_time: dayjs(this.datePickerService.endDate()).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      is_dummy: 0 as const
    };

    this.apiService.getContractDetails(payload).subscribe({
      next: (res) => {
        const filtered = this.filterContractsByAdmin(res, this.adminId());
        const mappedData = this.contractStatusService.calculateContractStatus(filtered);
        this.contractStatusData.set(mappedData);
        // Trigger resize to ensure pie chart redraws properly with the new dimensions
        setTimeout(() => window.dispatchEvent(new Event('resize')), 150);

        const gapData = this.contractStatusService.calculateExecutionGap(filtered);
        this.executionGapData.set(gapData);
        
        const timelineDist = this.contractStatusService.calculateTimelineDistribution(filtered);
        this.timelineData.set(timelineDist);
        
        const valData = this.contractStatusService.calculateSuccessByValue(filtered);
        this.successByValueData.set(valData);

        this.updateRiskMetricsFromContractData(filtered);
      },
      error: (err) => {
        console.error('Error fetching contract details:', err);
        // Fallback or empty state if needed
      }
    });
  }

  private fetchDuplicatesData() {
    const payload = {
      start_time: dayjs(this.datePickerService.startDate()).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      end_time: dayjs(this.datePickerService.endDate()).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      is_dummy: 0 as const,
      include_records: true
    };

    this.apiService.getDuplicatesByAdmin(payload).subscribe({
      next: (res) => {
        const adminId = this.adminId();
        const adminRecord = res.records?.find(r => r.admin_id === adminId);
        
        if (adminRecord) {
          this.dataQualityRiskData.set([
            { label: 'Null Entries', value: adminRecord.total_null_entries || 0, color: '#6889f8' },
            { label: 'Duplicates', value: adminRecord.duplicate_entries || 0, color: '#00dc82' },
            { label: 'Deletion', value: adminRecord.deletion_entries || 0, color: '#f5a829' }
          ]);
        } else {
          this.dataQualityRiskData.set([
            { label: 'Null Entries', value: 0, color: '#6889f8' },
            { label: 'Duplicates', value: 0, color: '#00dc82' },
            { label: 'Deletion', value: 0, color: '#f5a829' }
          ]);
        }
      },
      error: (err) => {
        console.error('Error fetching duplicates by admin:', err);
      }
    });
  }

  private updateRiskMetricsFromContractData(res: any) {
    const metrics = this.contractStatusService.calculateRiskMetrics(res);

    this.riskMetrics.update(r => ({
      ...r,
      duplicateRate: metrics.duplicateRate,
      deletionRate: metrics.deletionRate,
      contractsWithDuplicates: metrics.contractsWithDuplicates,
      duplicateEntries: metrics.duplicateEntries,
      deletionEntries: metrics.deletionEntries,
      delinquency90PlusContracts: metrics.delinquency90PlusContracts,
      delinquency90PlusRate: metrics.delinquency90PlusRate,
      overdueRate: metrics.overdueRate,
      portfolioHealth: metrics.portfolioHealth
    }));

    // Data Quality Risk mapping moved to fetchDuplicatesData()

    // Update the Risk Index KPI which depends on contract logic
    let riskIndexValue = 'Low';
    let riskColorClass = 'text-[#15B268]'; // Default green for low

    if (metrics.overdueRate > 0.5) {
      riskIndexValue = 'High';
      riskColorClass = 'text-[#F05050]'; // Red for high
    } else if (metrics.overdueRate > 0.3) {
      riskIndexValue = 'Medium';
      riskColorClass = 'text-[#F59E0B]'; // Amber for medium
    }

    this.kpis[4].value = riskIndexValue;
    this.kpis[4].label = 'Risk Index'; 
    this.kpis[4].icon = 'TriangleAlert';
    this.kpis[4].colorClass = riskColorClass;
    this.kpis[4].trend = '';
    this.kpis[4].trendIcon = '';

    this.scorecard.update(s => ({
      ...s,
      uniqueContracts: metrics.uniqueContractsCount,
      arrangementsPerContract: metrics.uniqueContractsCount > 0 ? Number((s.totalArrangements / metrics.uniqueContractsCount).toFixed(2)) : 0
    }));

    this.adminInfo.update(a => ({
      ...a,
      uniqueContracts: metrics.uniqueContractsCount.toString()
    }));
  }

  private mapExecutionGapData(res: AdminDetailAPI) {
    // Handled dynamically
  }

  private mapSuccessByValueData(res: AdminDetailAPI) {
    // Handled dynamically
  }

  /**
   * Returns a filtered copy of ContractDetailsAPI containing only contracts
   * that have at least one entry_detail or active_arrangement created by the given admin.
   * When adminId is falsy the original data is returned unchanged.
   */
  private filterContractsByAdmin(
    data: ContractDetailsAPI | null | undefined,
    adminId: number
  ): ContractDetailsAPI | null | undefined {
    if (!data || !adminId) return data;

    const filtered = data.contracts.filter(c =>
      (c.entry_details ?? []).some((e: any) => e.admin_id === adminId)
      || c.active_arrangement?.admin_id === adminId
    );

    return { ...data, contracts: filtered, total_contracts: filtered.length };
  }

  private updateDynamicPerformanceTrend(res: AdminDetailAPI) {
    const startStr = this.datePickerService.startDate();
    const endStr = this.datePickerService.endDate();
    const start = dayjs(startStr).tz(LA_TIMEZONE);
    const end = dayjs(endStr).tz(LA_TIMEZONE);
    const totalDays = end.diff(start, 'day') + 1;
    const startMonth = start.startOf('month');
    const endMonth = end.startOf('month');
    const totalMonths = endMonth.diff(startMonth, 'month') + 1;

    let labels: string[] = [];
    type IntervalType = 'all' | 'month' | 'quarter' | 'year';
    let intervalType: IntervalType = 'all';

    if (totalDays <= 14) {
      intervalType = 'all';
      labels = [start.format('MMM YYYY')];
    } else if (totalMonths <= 6) {
      intervalType = 'month';
      for (let i = 0; i < totalMonths; i++) {
        labels.push(startMonth.add(i, 'month').format('MMM YYYY'));
      }
    } else {
      const startQuarter = start.startOf('quarter');
      const endQuarter = end.startOf('quarter');
      const totalQuarters = endQuarter.diff(startQuarter, 'quarter') + 1;

      if (totalQuarters <= 5) {
        intervalType = 'quarter';
        for (let i = 0; i < totalQuarters; i++) {
          const qStart = startQuarter.add(i, 'quarter');
          labels.push(`Q${Math.floor(qStart.month() / 3) + 1} ${qStart.year()}`);
        }
      } else {
        intervalType = 'year';
        const startYear = start.startOf('year');
        const endYear = end.startOf('year');
        const totalYears = Math.min(5, endYear.diff(startYear, 'year') + 1);
        for (let i = 0; i < totalYears; i++) {
          labels.push(startYear.add(i, 'year').format('YYYY'));
        }
      }
    }

    const records = res.creation_trend?.records || [];
    const labelData: Record<string, { success: number; fail: number }> = {};
    labels.forEach(l => labelData[l] = { success: 0, fail: 0 });

    records.forEach(r => {
      // Depending on API, date is either in start/end or unit_date depending on payload interval
      const rDate = dayjs(r['start'] || r.unit_date);
      let matchLabel = '';
      
      if (intervalType === 'all') {
        matchLabel = start.format('MMM YYYY');
      } else if (intervalType === 'month') {
        matchLabel = rDate.format('MMM YYYY');
      } else if (intervalType === 'quarter') {
        matchLabel = `Q${Math.floor(rDate.month() / 3) + 1} ${rDate.year()}`;
      } else if (intervalType === 'year') {
        matchLabel = rDate.format('YYYY');
      }

      if (labelData[matchLabel]) {
        labelData[matchLabel].success += (r['successful'] || 0);
        labelData[matchLabel].fail += (r['failed'] || 0);
      }
    });

    let hasAnyData = false;

    const trendData = labels.map(label => {
      const data = labelData[label];
      const total = data.success + data.fail;
      const successPct = total > 0 ? parseFloat(((data.success / total) * 100).toFixed(2)) : 0;
      const failPct = total > 0 ? parseFloat(((data.fail / total) * 100).toFixed(2)) : 0;
      
      if (total > 0) hasAnyData = true;

      return {
        label,
        value: 100, // Full ring limit
        segments: [
          { label: 'Success', value: successPct, color: '#15B268' },
          { label: 'Failed', value: failPct, color: '#F05050' }
        ]
      };
    });

    const titleSuffix = intervalType === 'all' ? 'Month' :
                        intervalType === 'month' ? 'Month' :
                        intervalType === 'quarter' ? 'Quarter' : 'Year';

    this.performanceTrendTitle.set(`Performance Trend by ${titleSuffix}`);
    this.performanceTrendSubtitle.set(`Total Successful, Total Failed and Success % by ${titleSuffix}`);

    this.hasPerformanceTrendData.set(hasAnyData);
    this.performanceTrendData.set(trendData);
  }
}
